import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'

import { getFormattedDate, getLocaleCurrencyString } from '../utils'
import {
  employerInvoiceTransactionLineItemDescription,
  type InvoiceItem,
  type Substantiation,
} from '../utils/employer_invoice_utils'
import Badge from './reusable/Badge'
import TanstackTable from './reusable/table/TanstackTable'

type Money = {
  value: string;
  currency: string;
}

type Transaction = {
  id: string;
  member_id: string;
  direction: string;
  amount: Money;
  balance: Money;
  summary: string;
  tags: string;
  remote_type: string;
  subtype_type: string;
  created_at: string;
  updated_at: string;
  card_last_4_digits?: string;
  merchant_name?: string;
  merchant_type?: string;
  rich_merchant_name?: string;
  rich_merchant_category_name?: string;
  substantiation: Substantiation;
  invoice_items: InvoiceItem[];
}

type DenormalizedTransaction = Transaction & {
  createdAt: Date;
  updatedAt: Date;
}

const CategoryBadge = ({ category }: { category: string }) => {
  if (category.includes('Premium')) {
    return <Badge text={category} type='success' />
  }

  if (category.includes('QME')) {
    return <Badge text={category} type='attention' />
  }

  if (category.includes('Post-tax')) {
    return <Badge text={category} type='info' />
  }

  return <Badge text={category} />
}

const columnHelper = createColumnHelper<DenormalizedTransaction>()

const columns = [
  columnHelper.accessor('created_at', {
    id: 'createdAt',
    header: 'Date',
    cell: ({ getValue }) => {
      return <div>{getFormattedDate(getValue<string>())}</div>
    },
    enableSorting: true,
    sortingFn: 'datetime',
  }),
  columnHelper.accessor('summary', {
    id: 'summary',
    header: 'Description',
    cell: ({ getValue }) => {
      return <div className='w-96 truncate'>{getValue<string>()}</div>
    },
    enableSorting: true,
    sortingFn: 'datetime',
  }),
  columnHelper.display({
    id: 'category',
    header: () => <div style={{ textAlign: 'left' }}>Category</div>,
    cell: ({ row }) => {
      const transaction = row.original
      const category =
        employerInvoiceTransactionLineItemDescription(transaction)

      return (
        <div className='truncate' style={{ textAlign: 'left' }}>
          <CategoryBadge category={category} />
        </div>
      )
    },
  }),
  columnHelper.accessor('amount', {
    header: () => <div style={{ textAlign: 'end' }}>Amount</div>,
    cell: ({ getValue, row }) => {
      const amount: Money = getValue<Money>()
      const { original } = row
      return (
        <div
          className={clsx({
            'text-green-500': original.direction === 'credit',
          })}
          style={{ textAlign: 'end' }}
        >
          {getLocaleCurrencyString(parseFloat(amount.value))}
        </div>
      )
    },
  }),
]

function MemberTransactions () {
  const [transactionsData, setTransactionsData] = React.useState<
    DenormalizedTransaction[]
  >([])
  const [isLoading, setIsLoading] = useState(true)

  const table = useReactTable({
    columns,
    data: transactionsData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
      sorting: [{ id: 'createdAt', desc: true }],
    },
  })

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
        const response = await fetch('/api/member_transactions', { // eslint-disable-line thatch/no-bare-fetch
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        })
        const json = await response.json()
        const { transactions }: { transactions: Transaction[] } = json.data

        setTransactionsData(denormalizeTransactions(transactions))
        setIsLoading(false)
      } catch (e) {
        // TODO: Figure out error state handling.
        setIsLoading(false)
        console.error(e)
      }
    }

    fetchEmployees()
  }, [table])

  if (isLoading) {
    <div>LOADING</div>
  }

  return (
    <div>
      <div className='h-8' />
      <TanstackTable
        table={table}
        paginationLabelSingular='transaction'
        isLoading={isLoading}
        rowClasses='py-4'
      />
    </div>
  )
}

// denormalizeTransactions updates each transaction with additional fields used for simpler display of transaction data.
function denormalizeTransactions (
  txns: Transaction[]
): DenormalizedTransaction[] {
  return txns.map((txn) => ({
    ...txn,
    createdAt: new Date(txn.created_at),
    updatedAt: new Date(txn.updated_at),
  }))
}

export default MemberTransactions
